import { useId, type ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { CircleAlert, decorativeIcon } from '../../lib/icons';
import { FieldContext } from './context';

/**
 * The label / help / error wiring, done once.
 *
 * react-hook-form hands you an error string; getting from there to a control a
 * screen reader announces correctly needs three linked ids and an
 * `aria-describedby` whose shape changes depending on which of help and error
 * is present. Every screen getting that right independently is not realistic,
 * so `FormField` computes it and each control reads it out of context.
 *
 * The error rules come straight from DESIGN.md: the field KEEPS ITS VALUE, the
 * message sits below with an `alert-circle` at 14 px, and it is a sentence
 * naming the fix — never "Champ invalide", never an error code.
 */
export interface FormFieldProps {
  /** The visible label, already translated. */
  readonly label: ReactNode;
  /** Helper text below the control. */
  readonly help?: ReactNode;
  /** A sentence naming the fix. Its presence is what marks the field invalid. */
  readonly error?: string;
  readonly required?: boolean;
  /** Rendered beside the label — "Optionnel", a `SourceChip`. Translated. */
  readonly labelAdornment?: ReactNode;
  /** Hides the label visually but keeps it for assistive technology. */
  readonly hideLabel?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}

export function FormField({
  label,
  help,
  error,
  required = false,
  labelAdornment,
  hideLabel = false,
  className,
  children,
}: FormFieldProps) {
  const base = useId();
  const controlId = `${base}-control`;
  const helpId = `${base}-help`;
  const errorId = `${base}-error`;

  const describedBy =
    [help !== undefined ? helpId : null, error !== undefined ? errorId : null]
      .filter((id): id is string => id !== null)
      .join(' ') || undefined;

  return (
    <FieldContext.Provider
      value={{ controlId, describedBy, invalid: error !== undefined, required }}
    >
      <div className={cn('flex flex-col gap-1.5', className)}>
        <div className={cn('flex items-baseline justify-between gap-2', hideLabel && 'sr-only')}>
          <label htmlFor={controlId} className="text-label uppercase text-ink-muted">
            {label}
            {required ? (
              <span aria-hidden="true" className="ms-1 text-status-danger-ink">
                {'*'}
              </span>
            ) : null}
          </label>
          {/*
            * `leading-none`: the adornment is usually `text-support`, whose
            * 1.5 line box is ~7px taller than the label's — enough to push
            * this field's control below its grid-row neighbours' (the zone
            * field sat visibly lower than site and date).
            */}
          {labelAdornment !== undefined ? (
            <span className="shrink-0 leading-none">{labelAdornment}</span>
          ) : null}
        </div>

        {children}

        {help !== undefined ? (
          <p id={helpId} className="text-support text-ink-muted">
            {help}
          </p>
        ) : null}

        {error !== undefined ? (
          <p
            id={errorId}
            // `role="alert"` would interrupt a worker mid-keystroke. The error
            // is already in `aria-describedby`; polite is the honest level.
            role="status"
            className="flex items-start gap-1.5 text-support text-status-danger-ink"
          >
            <CircleAlert {...decorativeIcon('xs')} className="mt-px shrink-0" />
            <span>{error}</span>
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}
